import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, productImage } from "../lib/brand";
import { useCart } from "../lib/cart";
import { useAuth, authHeaders } from "../lib/auth";
import { Trash2, ArrowRight, ShoppingBag, User } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, total } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    // eslint-disable-next-line
  }, [user]);

  const handleCheckout = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/checkout/create-session`,
        {
          items: items.map((i) => ({ product_id: i.id })),
          email,
          origin_url: window.location.origin,
        },
        { headers: authHeaders() },
      );
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div data-testid="cart-page" className="pt-32 pb-24 max-w-5xl mx-auto px-6 lg:px-12">
      <div className="border-b border-[#1A2B20] pb-8 mb-10">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-3">Checkout</div>
        <h1 className="font-serif-display text-5xl text-white">Your selection</h1>
      </div>

      {items.length === 0 ? (
        <div data-testid="cart-empty" className="border border-dashed border-[#1A2B20] p-16 text-center">
          <ShoppingBag className="w-10 h-10 text-[#5C7064] mx-auto mb-4" />
          <p className="text-[#95A89C] mb-6">Your cart is empty.</p>
          <Link to="/music" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase">
            Browse the collection <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {items.map((p) => (
              <div key={p.id} data-testid={`cart-item-${p.id}`} className="flex gap-4 p-4 bg-[#09120C] border border-[#1A2B20]">
                <div className="relative w-24 h-24 bg-black flex-shrink-0 overflow-hidden">
                  <img
                    src={productImage(p)}
                    alt={p.title}
                    className="w-full h-full object-cover"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  <div className="absolute bottom-1 right-1 bg-black px-1 py-0.5">
                    <img
                      src={`${API}/brand/wordmark.png`}
                      alt="Grá"
                      className="h-3 w-auto object-contain"
                    />
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">{p.type}</div>
                    <div className="font-serif-display text-xl text-white">{p.title}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="font-serif-display text-lg text-white">${Number(p.price).toFixed(2)}</div>
                    <button
                      data-testid={`cart-remove-${p.id}`}
                      onClick={() => remove(p.id)}
                      className="text-[#95A89C] hover:text-red-400 p-2"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-32 p-6 bg-[#09120C] border border-[#1A2B20]">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-4">Order summary</div>
              <div className="flex justify-between text-sm text-[#95A89C] mb-2">
                <span>Items</span><span>{items.length}</span>
              </div>
              <div className="flex justify-between font-serif-display text-2xl text-white border-t border-[#1A2B20] pt-4 mt-4">
                <span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span>
              </div>

              <div className="mt-6">
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Email for receipt</label>
                <input
                  data-testid="cart-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@domain.com"
                  className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none"
                />
              </div>

              <button
                data-testid="cart-checkout-button"
                disabled={loading}
                onClick={handleCheckout}
                className="mt-6 w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1E7040] hover:bg-[#165932] disabled:opacity-50 text-white text-xs tracking-[0.3em] uppercase transition-colors"
              >
                {loading ? "Redirecting…" : <>Pay with Stripe <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="mt-4 text-[10px] text-[#5C7064] leading-relaxed">
                Secure payment via Stripe. You'll get a thank-you email after purchase.
              </p>
              {!user && (
                <div className="mt-4 pt-4 border-t border-[#1A2B20]">
                  <p className="text-[10px] text-[#5C7064] mb-2 tracking-wider uppercase">Checking out as guest</p>
                  <Link
                    to="/login"
                    data-testid="cart-login-prompt"
                    className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#279153] hover:underline"
                  >
                    <User className="w-3 h-3" /> Login to track orders
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
